import { useMemo } from 'react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { getSaleQuote } from '@/api/sale.api';
import { getProductAvailability } from '@/api/ticketSchedule.api';
import { usePaymentMethods } from '@/hooks/queries';
import { buildSalePayload } from '@/lib/buildSalePayload';
import { getBookableSlots } from '@/lib/slotAvailability';
import { productsNeedingStandIn, withStandInSlots } from '@/lib/quoteSlots';
import { useCartStore } from '@/stores/cart.store';
import type { PaymentMethod } from '@/types/payment';

/**
 * The method a quote is priced with. Prefer a non-e-money method: e-money
 * requires a reader on billingInfo, which isn't chosen until the payment
 * dialog. Price is method-independent except for PAYMENT-gated promos, which
 * surface against this placeholder (the payment dialog re-prices card tenders).
 */
export function pickQuoteMethod(methods: PaymentMethod[]): PaymentMethod | undefined {
  return methods.find((m) => m.paymentGateway !== 'e-money') ?? methods[0];
}

/**
 * Live BE-authoritative quote of the cart (`POST /sale/quote`) — what the sale
 * will charge, discounts included. One author for the Jual cart, the Details
 * summary and Checkout, so all three show the same figure and share one cache
 * entry for the same cart.
 *
 * Before the cashier picks time slots the quote is priced against stand-in
 * slots (`lib/quoteSlots`) and `estimated` is true. `quote` is null when the
 * quote cannot run yet (empty cart, slots still loading, no payment method)
 * or failed — callers fall back to their local estimate, as before.
 *
 * `keepPrevious` holds the last figure on screen while a changed cart
 * re-quotes, instead of flashing back to the undiscounted estimate. Display
 * only: Checkout leaves it off, because its total is what the payment dialog
 * takes as the amount due and must never be a stale one.
 */
export function useCartQuote({
  enabled = true,
  keepPrevious = false,
}: { enabled?: boolean; keepPrevious?: boolean } = {}) {
  const { lines, bundleLines, customer, visitDate, couponCode, discountIds, promo, mode, focDocument } =
    useCartStore(
      useShallow((s) => ({
        lines: s.lines,
        bundleLines: s.bundleLines,
        customer: s.customer,
        visitDate: s.visitDate,
        couponCode: s.couponCode,
        discountIds: s.discountIds,
        promo: s.promo,
        mode: s.mode,
        focDocument: s.focDocument,
      })),
    );
  const focDocumentId = mode === 'foc' ? (focDocument?.id ?? null) : null;
  const cartCount = lines.length + bundleLines.length;

  const { data: methods = [] } = usePaymentMethods();
  const quoteMethod = useMemo(() => pickQuoteMethod(methods), [methods]);

  // Same key as useAvailability / useCatalogAvailability → shared cache entry.
  const standInIds = useMemo(() => productsNeedingStandIn(lines, bundleLines), [lines, bundleLines]);
  const availability = useQueries({
    queries: standInIds.map((id) => ({
      queryKey: ['availability', id, visitDate],
      queryFn: () => getProductAvailability(id, visitDate),
      enabled: enabled && Boolean(visitDate),
      staleTime: 15_000,
    })),
  });
  const standInKey = availability
    .map((q, i) => `${standInIds[i]}:${q.data ? (getBookableSlots(q.data.slots, { visitDate })[0]?.id ?? '-') : '?'}`)
    .join('|');
  const standIns = useMemo(() => {
    const map = new Map<string, string>();
    for (const part of standInKey ? standInKey.split('|') : []) {
      const [id, slot] = part.split(':');
      if (id && slot && slot !== '-' && slot !== '?') map.set(id, slot);
    }
    return map;
  }, [standInKey]);

  const prepared = useMemo(() => {
    if (!quoteMethod || cartCount === 0) return null;
    const filled = withStandInSlots(lines, bundleLines, (id) => standIns.get(id));
    if (filled.blocked) return null;
    const payload = buildSalePayload({
      lines: filled.lines,
      bundleLines: filled.bundleLines,
      customer: { ...customer, name: customer.name.trim() || 'Guest' },
      visitDate,
      couponCode,
      discountIds,
      paymentMethodId: quoteMethod.id,
      paymentGateway: quoteMethod.paymentGateway,
      // Placeholder reader only when the sole method is e-money (validator needs it).
      reader: quoteMethod.paymentGateway === 'e-money' ? { mid: '00000', tid: '00000' } : undefined,
      groupType: promo.groupType,
      sourceChannel: promo.sourceChannel,
      identity: promo.identity,
      documentScanToken: promo.documentScanToken,
      focDocumentId,
    });
    return { payload, estimated: filled.usedStandIn };
  }, [
    quoteMethod,
    cartCount,
    lines,
    bundleLines,
    standIns,
    customer,
    visitDate,
    couponCode,
    discountIds,
    promo,
    focDocumentId,
  ]);

  const query = useQuery({
    queryKey: ['saleQuote', prepared?.payload ?? null],
    queryFn: () => getSaleQuote(prepared!.payload),
    enabled: enabled && Boolean(prepared),
    staleTime: 10_000,
    retry: false,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });

  return {
    query,
    quote: prepared ? (query.data ?? null) : null,
    estimated: prepared?.estimated ?? false,
    quoteMethod,
  };
}
